import React, { useEffect, useState, useRef, useCallback } from "react";
import { styled } from "../stitches.config";
import { AnyBlock, Point2D, Position, Size } from "../types";
import FaceBlock from "./FaceBlock";
import WebBlock from "./WebBlock";
import TextBlock from "./TextBlock";
import UndefinedBlock from "./UndefinedBlock";
import ImageBlock from "./ImageBlock";
import CssBlock from "./CssBlock";
import {
  addSize,
  gridToPixelPosition,
  gridToPixelSize,
  pixelToGridPosition,
  pixelToGridPositionSloppy,
  pixelToGridSize,
  pixelToGridSizeSloppy,
} from "../utils/gridPosition";
import { GRID_SEAM } from "../consts";
import {
  updateBlockMap,
  useMapWrapper,
  useUserInfo,
} from "../roomservice/maps";
import { LinkBlock } from "./LinkBlock";
import { blockSelectionAtom, useCanvasRef } from "../jotai/atoms";
import { useMouseRef } from "../jotai/atoms";
import useThrottle from "../utils/useThrottle";
import { useUpdateAtom } from "jotai/utils";
import chroma from "chroma-js";
import ImageSearchBlock from "./ImageSearchBlock";
import { COLORS } from "../ui/user/user";

enum Corners {
  "top-right" = "top-right",
  "bottom-right" = "bottom-right",
  "top-left" = "top-left",
  "bottom-left" = "bottom-left",
}

function Block({
  id,
  upsertBlock,
  deleteBlock,
  selected,
  justCreated,
}: {
  id: string;
  upsertBlock: (b: AnyBlock) => void;
  deleteBlock: (blockId: string) => void;
  selected: boolean;
  justCreated: boolean;
}) {
  const mousePositionRef = useMouseRef();
  const canvasRef = useCanvasRef();
  const [block, blockClient] = useMapWrapper<AnyBlock>(id);
  let [user] = useUserInfo(block?.madeBy);

  const updateBlock = useCallback(
    function (updates: Partial<AnyBlock>) {
      if (blockClient) updateBlockMap(blockClient, updates);
    },
    [blockClient]
  );

  const updateBlockThrottled = useThrottle(updateBlock, 1000 / 30);

  const [isMoving, setIsMoving] = useState<boolean>(false);
  const mouseOffsetInBlockRef = useRef<Point2D>({ x: 0, y: 0 });

  const selectBlock = useUpdateAtom(blockSelectionAtom);

  // Select or Move block
  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!block || !mousePositionRef) return;
      // Prevent default "image ghost" interaction on images.
      e.preventDefault();
      let multiSelect = e.shiftKey || e.ctrlKey || e.metaKey;
      selectBlock({ type: multiSelect ? "add" : "set", id: block.id });

      setIsMoving(true);
      const blockPixelPos = gridToPixelPosition(block.position);
      mouseOffsetInBlockRef.current = {
        x: mousePositionRef.current.x - blockPixelPos.x,
        y: mousePositionRef.current.y - blockPixelPos.y,
      };
    },
    [block, mousePositionRef, selectBlock]
  );

  function onMouseUp(e: React.MouseEvent) {
    if (isMoving) setIsMoving(false);
  }

  useEffect(() => {
    if (!block || !canvasRef || !mousePositionRef) return;
    function onMouseMove(e: MouseEvent) {
      if (!isMoving || !block) return;
      updateBlockThrottled({
        position: pixelToGridPositionSloppy({
          x: mousePositionRef!.current.x - mouseOffsetInBlockRef.current.x,
          y: mousePositionRef!.current.y - mouseOffsetInBlockRef.current.y,
        }),
      });
    }

    function onMouseUp(e: MouseEvent) {
      if (isMoving && block) {
        updateBlock({
          position: pixelToGridPosition(
            {
              x: mousePositionRef!.current.x - mouseOffsetInBlockRef.current.x,
              y: mousePositionRef!.current.y - mouseOffsetInBlockRef.current.y,
            },
            true
          ),
        });
      }

      if (isMoving) setIsMoving(false);
    }

    if (isMoving) {
      canvasRef.addEventListener("mousemove", onMouseMove);
      canvasRef.addEventListener("mouseup", onMouseUp);
    }

    return () => {
      canvasRef.removeEventListener("mousemove", onMouseMove);
      canvasRef.removeEventListener("mouseup", onMouseUp);
    };
  }, [
    canvasRef,
    block,
    isMoving,
    updateBlock,
    mousePositionRef,
    updateBlockThrottled,
  ]);

  // Resize
  const [resizingCorner, setResizingCorner] = useState<Corners | null>();
  const resizeStartSize = useRef<Size>({ width: 0, height: 0 });
  const resizeStart = useRef<Point2D>({ x: 0, y: 0 });

  function handleResizeStart(corner: Corners, e: React.MouseEvent) {
    if (!block || !mousePositionRef) return;
    e.stopPropagation(); // Prevent activating move
    e.preventDefault();

    const blockPixelSize = gridToPixelSize(block.size);
    const blockPixelPos = gridToPixelPosition(block.position);

    resizeStart.current.x = mousePositionRef.current.x;
    resizeStart.current.y = mousePositionRef.current.y;

    resizeStartSize.current.width = blockPixelSize.width;
    resizeStartSize.current.height = blockPixelSize.height;

    mouseOffsetInBlockRef.current = {
      x: mousePositionRef.current.x - blockPixelPos.x,
      y: mousePositionRef.current.y - blockPixelPos.y,
    };

    setResizingCorner(corner);
  }

  useEffect(() => {
    if (!block || !canvasRef || !mousePositionRef) return;

    function handleResize({ sloppy = false, throttled = false } = {}) {
      if (!resizingCorner || !block || !resizeStartSize.current) return;

      let newSize = gridToPixelSize(block.size);
      let newPosition = gridToPixelPosition(block.position);

      const localPixelToGridSize = sloppy
        ? pixelToGridSizeSloppy
        : (size: Size) => pixelToGridSize(size, true);

      const localPixelToGridPosition = sloppy
        ? pixelToGridPositionSloppy
        : (position: Position) => pixelToGridPosition(position, true);

      const localUpdateBlock = throttled ? updateBlockThrottled : updateBlock;

      const mx = mousePositionRef!.current.x;
      const my = mousePositionRef!.current.y;

      const mxOff = mx - mouseOffsetInBlockRef.current.x;
      const myOff = my - mouseOffsetInBlockRef.current.y;

      switch (resizingCorner) {
        case Corners["bottom-right"]:
          newSize = addSize(
            {
              width: mx - resizeStart.current.x,
              height: my - resizeStart.current.y,
            },
            resizeStartSize.current
          );
          break;

        case Corners["top-right"]:
          newPosition.y = myOff;
          newSize = addSize(
            {
              width: mx - resizeStart.current.x,
              height: -(my - resizeStart.current.y),
            },
            resizeStartSize.current
          );
          break;

        case Corners["bottom-left"]:
          newPosition.x = mxOff;

          newSize = addSize(
            {
              width: -(mx - resizeStart.current.x),
              height: my - resizeStart.current.y,
            },
            resizeStartSize.current
          );

          break;

        case Corners["top-left"]:
          newPosition.x = mxOff;
          newPosition.y = myOff;

          newSize = addSize(
            {
              width: -(mx - resizeStart.current.x),
              height: -(my - resizeStart.current.y),
            },
            resizeStartSize.current
          );
          break;
      }

      localUpdateBlock({
        size: localPixelToGridSize(newSize),
        position: localPixelToGridPosition(newPosition),
      });
    }

    function onMouseMove(e: MouseEvent) {
      if (resizingCorner) {
        e.preventDefault(); // Prevent drag selecting
      }

      handleResize({ sloppy: true, throttled: true });
    }

    function onMouseUp() {
      handleResize({ sloppy: false, throttled: false });
      setResizingCorner(null);
    }

    if (resizingCorner) {
      canvasRef.addEventListener("mousemove", onMouseMove);
      canvasRef.addEventListener("mouseup", onMouseUp);
    }

    return () => {
      canvasRef.removeEventListener("mousemove", onMouseMove);
      canvasRef.removeEventListener("mouseup", onMouseUp);
    };
  }, [
    canvasRef,
    block,
    resizingCorner,
    updateBlock,
    mousePositionRef,
    updateBlockThrottled,
  ]);

  // FIXME: RS doesn't allow us to set all properties at once
  // so occasionally you may receive a new block with only some of its properties
  // RS is working on this, but it's tricky bc of transactions
  if (!block || !isBlockFullyFormed(block)) return <>Loading block...</>;

  const pos = gridToPixelPosition(block.position);
  const size = gridToPixelSize(block.size);
  const seamStyle = {
    transform: `translate(${pos.x}px, ${pos.y}px)`,
    zIndex: timestampToZIndex(block.updatedAt),
    width: `${size.width}px`,
    height: `${size.height}px`,
  };
  let transparent = block.type === "image" && block.value !== undefined;
  return (
    <Seam style={seamStyle}>
      <BlockView
        className={`block block-${block.type} ${transparent ? "" : user?.name}`}
        style={{
          cursor: isMoving ? "grabbing" : "grab",
          backgroundColor: transparent
            ? "transparent"
            : chroma(user?.color || COLORS[0])
                .desaturate(0.1)
                .brighten(0.1)
                .css(),
        }}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        selected={selected}
        moving={isMoving}
        transparent={transparent}
      >
        <BlockFactory
          block={block}
          justCreated={justCreated}
          updateBlock={updateBlock}
          upsertBlock={upsertBlock}
          deleteBlock={deleteBlock}
        />
        {Object.keys(Corners).map((corner) => (
          <Resize
            key={corner}
            onMouseDown={(e) => handleResizeStart(corner as Corners, e)}
            corner={corner as Corners}
          />
        ))}
      </BlockView>
    </Seam>
  );
}

export default React.memo(Block);

export const Resize = styled("div", {
  position: "absolute",
  width: "$4",
  height: "$4",
  background: "transparent",

  variants: {
    corner: {
      "bottom-right": {
        bottom: "-$1",
        right: "-$1",
        cursor: "nwse-resize",
      },

      "top-right": {
        top: "-$1",
        right: "-$1",
        cursor: "nesw-resize",
      },

      "bottom-left": {
        bottom: "-$1",
        left: "-$1",
        cursor: "nesw-resize",
      },

      "top-left": {
        top: "-$1",
        left: "-$1",
        cursor: "nwse-resize",
      },
    },
  },
});

const Seam = styled("div", {
  padding: GRID_SEAM / 2,
  position: "absolute",
  willChange: "transform",
});

export const BlockView = styled("li", {
  height: "100%",
  width: "100%",
  display: "grid",
  color: "$brown",
  pointerEvents: "auto", // remove hack later (see blocklist ul)
  transition: "filter 0.2s ",
  transitionTimingFunction: "cubic-bezier(0.32, 0.08, 0.24, 1.0)",
  cursor: "grab",
  variants: {
    transparent: {
      true: {
        background: "transparent",
        border: "1px solid transparent",
      },
      false: {
        border: "1px solid $menuBeige",
      },
    },
    selected: {
      true: {
        border: "1px solid $menuBeige",
        "&::before": {
          content: `""`,
          display: "block",
          position: "absolute",
          top: "0",
          bottom: "0",
          right: "0",
          left: "0",
          border: "1px solid $transparentBrown40",
          pointerEvents: "none",
        },
      },
    },
    moving: {
      true: {
        filter: "drop-shadow(0 4px 12px $colors$transparentBlack10)",
      },
      false: {
        filter: "drop-shadow(0 2px 4px $colors$transparentBlack05)",
      },
    },
  },
});

function BlockFactory({
  block,
  updateBlock,
  upsertBlock,
  deleteBlock,
  justCreated,
}: {
  block: AnyBlock;
  updateBlock: (block: Partial<AnyBlock>) => void;
  upsertBlock: (block: AnyBlock) => void;
  deleteBlock: (blockId: string) => void;
  justCreated: boolean;
}) {
  switch (block.type) {
    case "web":
      return (
        <WebBlock
          block={block}
          updateBlock={updateBlock}
          upsertBlock={upsertBlock}
        />
      );
    case "face":
      return <FaceBlock block={block} updateBlock={updateBlock} />;
    case "text":
      return (
        <TextBlock
          block={block}
          justCreated={justCreated}
          updateBlock={updateBlock}
          deleteBlock={deleteBlock}
        />
      );
    case "imageSearch":
      return (
        <ImageSearchBlock
          block={block}
          justCreated={justCreated}
          updateBlock={updateBlock}
        />
      );
    case "image":
      return (
        <ImageBlock
          block={block}
          justCreated={justCreated}
          updateBlock={updateBlock}
        />
      );
    case "link":
      return <LinkBlock block={block} updateBlock={updateBlock} />;
    case "css":
      return (
        <CssBlock
          block={block}
          updateBlock={updateBlock}
          deleteBlock={deleteBlock}
        />
      );
    default:
      return <UndefinedBlock block={block} updateBlock={updateBlock} />;
  }
}

function isBlockFullyFormed(block: Partial<AnyBlock>) {
  return (
    block.hasOwnProperty("id") &&
    block.hasOwnProperty("updatedAt") &&
    block.hasOwnProperty("position") &&
    block.hasOwnProperty("size")
  );
}

// Compresses timestamps to a smaller range for use as zIndexes.
// will run into problems around year 2027
const MakespaceEpochMillis: number = new Date("Jan 1, 2021").getTime();

export function timestampToZIndex(updatedAt: number) {
  return Math.round((updatedAt - MakespaceEpochMillis) / 100);
}
